/**
 * Admin V3 — Drivers — Detail page (Plan §2.2 M-F-4).
 * Phase 4 G5. Single authoritative driver-detail surface inside Admin V3.
 *
 * Tabs: Identity / Documents / Approval / Suspension / History.
 * Each tab is implemented as a separate panel component (M-F-5..M-F-9).
 *
 * Mounted at /admin-v3/drivers/:driverId — replaces DetailPlaceholder.
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, FileText, ShieldCheck, Pause, History } from 'lucide-react';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { lifecycleStateMeta, cn } from '../../shared/visualTokens';
import { t, DEFAULT_LOCALE } from '../../i18n';
import DriverIdentityPanel from './DriverIdentityPanel';
import DriverDocumentsPanel from './DriverDocumentsPanel';
import DriverApprovalWorkflowPanel from './DriverApprovalWorkflowPanel';
import DriverSuspensionPanel from './DriverSuspensionPanel';
import DriverComplianceHistoryPanel from './DriverComplianceHistoryPanel';
import DriverComplianceStatePanel from './DriverComplianceStatePanel';
import DriverActivationControl from './DriverActivationControl';

const TABS = [
  { id: 'identity',   labelKey: 'compliance.driverDetail.tab.identity',   icon: User },
  { id: 'documents',  labelKey: 'compliance.driverDetail.tab.documents',  icon: FileText },
  { id: 'approval',   labelKey: 'compliance.driverDetail.tab.approval',   icon: ShieldCheck },
  { id: 'suspension', labelKey: 'compliance.driverDetail.tab.suspension', icon: Pause },
  { id: 'history',    labelKey: 'compliance.driverDetail.tab.history',    icon: History },
];

const DriverDetailPage = () => {
  const locale = DEFAULT_LOCALE;
  const { driverId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('identity');

  const { data: driver, loading, error, refetch } = useAdminQuery(
    driverId ? `/admin/drivers/${driverId}` : null,
    {},
  );

  const lifecycle = (driver && driver.lifecycle_state) || null;
  const meta = lifecycle ? lifecycleStateMeta[lifecycle] : null;

  return (
    <div className="p-6 space-y-6" data-testid="driver-detail-page">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate('/admin/drivers')}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
          data-testid="driver-detail-back"
        >
          <ArrowLeft className="h-4 w-4" /> {t('nav.drivers', locale)}
        </button>
        <div className="flex items-center gap-3">
          {meta && (
            <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${meta.pill}`}>
              {t(meta.labelKey, locale)}
            </span>
          )}
          {/* P1-B3-3a: operational activation toggle (is_active), migrated from legacy V1. */}
          {driver && <DriverActivationControl driver={driver} locale={locale} onChange={refetch} />}
        </div>
      </div>

      <h1 className="text-[28px] font-semibold text-slate-900">
        {(driver && driver.name) || (driver && driver.email) || driverId}
      </h1>

      {loading && <p className="text-slate-500 text-sm">{t('compliance.common.loading', locale)}</p>}
      {error && <p className="text-rose-700 text-sm">{t('compliance.common.error', locale)}</p>}

      {/* Tab bar */}
      <div className="border-b border-slate-200 flex gap-1 overflow-x-auto">
        {TABS.map((tabDef) => {
          const Icon = tabDef.icon;
          const isActive = tab === tabDef.id;
          return (
            <button
              key={tabDef.id}
              type="button"
              onClick={() => setTab(tabDef.id)}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                isActive
                  ? 'border-slate-900 text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700',
              )}
              data-testid={`driver-detail-tab-${tabDef.id}`}
            >
              <Icon className="h-4 w-4" />
              {t(tabDef.labelKey, locale)}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {driver && tab === 'identity' && (
        <>
          <DriverIdentityPanel driver={driver} locale={locale} />
          {/* G11.F — State visibility: explain WHY the driver is in their
              current compliance_state + what's required to advance. */}
          <DriverComplianceStatePanel driver={driver} locale={locale} />
        </>
      )}
      {driver && tab === 'documents'  && <DriverDocumentsPanel driverId={driverId} locale={locale} />}
      {driver && tab === 'approval'   && <DriverApprovalWorkflowPanel driver={driver} locale={locale} onChange={refetch} />}
      {driver && tab === 'suspension' && <DriverSuspensionPanel driver={driver} locale={locale} onChange={refetch} />}
      {driver && tab === 'history'    && <DriverComplianceHistoryPanel driverId={driverId} locale={locale} />}
    </div>
  );
};

export default DriverDetailPage;
